<template>
  <div>
    <page-header
      :page="page"
      :actions="headerActionConfig"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <board class="mb-4" :details="generalInfoBoard" />
    <custom-error
      v-if="!detail.generalInfo?.hasActivity"
      is-alert-mode
      message="Estudiante no cuenta con actividad registrada."
    />
    <areas
      ref="detailAreas"
      class="mb-4"
      :items="detail.areas"
      @display="onDisplayArea"
    >
      <template #criteria-1="{ item} ">
        <backup-document
          :key="'backup-detail-' + item.key + '-' + documentsReloadKey"
          :read-only="readOnly"
          :area-id="item.area.areaId"
          :sub-area-id="item.area.subAreaId"
          :studentSieIdProp="detail.generalInfo?.studentSieId"
          :transition-student-id="id"
          :objKey="item.key"
          @files="(data) => gettingBackupDocument(data, item.key, item.area)"
          @remove="onDocumentsChange"
        />
      </template>
      <template #criteria-2="{ item }">
        <draggable
          v-model="item.area.strengths"
          tag="div"
          :group="`objectives-${item.key}`"
          :disabled="readOnly"
          @end="onChangeObjectives"
        >
          <div
            class="my-3 col-12"
            v-for="(strength, key) in item.area.strengths"
            :key="key"
          >
            <objective
              :read-only="readOnly"
              :area-index="item.key"
              :index="key"
              :objective="strength"
              @quit="onQuitObjective(key, item.area.strengths)"
            ></objective>
          </div>
        </draggable>
      </template>
      <template #criteria-3="{ item }">
        <draggable
          v-model="item.area.needs"
          tag="div"
          :group="`objectives-${item.key}`"
          :disabled="readOnly"
          @end="onChangeObjectives"
        >
          <div
            class="my-3 col-12"
            v-for="(need, key) in item.area.needs"
            :key="key"
          >
            <objective
              :read-only="readOnly"
              :area-index="item.key"
              :index="key"
              :objective="need"
              @quit="onQuitObjective(key, item.area.needs)"
            ></objective>
          </div>
        </draggable>
      </template>
    </areas>
    <form>
      <section-title content="Firma de Participantes" has-line class="mb-1" />
      <div
        v-if="readOnly"
        class="participant-signatures bg-secondary-3 border-radius--light"
      >
        <div
          v-for="(item, index) in detail.signatures"
          class="participant"
          :key="index"
        >
          <participant-signature-row :item="item" />
        </div>
      </div>
      <participant-signatures
        v-else-if="availableSignatureOptions.length > 0"
        ref="ParticipantSignatures"
        is-draft
        :key="componentKey"
        :value="signatureList"
        :options="availableSignatureOptions"
        :participation-types="participationTypeList"
        :alternate-participations="alternateParticipationTypeList"
        :info-message="signatureInfoMessage"
        @addSignature="addSignature()"
        @deleteSignature="deleteSignature()"
      />

      <div class="col-12">
        <ValidationObserver ref="termsResponsability">
          <div class="mt-5">
            <div class="mt-3 tag-badge pl-2">
              <span class="dot text-center">
                <i
                  class="far fa-check-circle orange-icon orange-icon-badge-align"
                ></i>
              </span>
              <span class="tag-badge-title py-1 pl-2">
                Certificación de procedimientos
              </span>
            </div>
          </div>
          <ValidationProvider name="email" v-slot="{ errors }" rules="required">
            <custom-checkbox-single-option
              :disabled="readOnly"
              v-model="termsAccepted"
              title="Certifico por este medio, que este referido podrá encontrarse en el expediente físico del estudiante firmado por todas las partes involucradas. (0502)"
              option-text="Acepto"
              :error="errors[0]"
              custom-error-msg="Favor de certificar haber revisado el historial de rechazos del servicio."
            />
          </ValidationProvider>
        </ValidationObserver>
      </div>
    </form>
  </div>
</template>

<script>
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import SectionTitle from 'SectionTitle';
import ParticipantSignatures from '@/components/pei/tabs/components/Tab9/participantSignatures/Tab9ParticipantSignatures';

import draggable from 'vuedraggable';
import PageHeader from 'PageHeader';
import Board from 'Board';
import {
  Areas,
  Objective,
  BackupDocument,
  ParticipantSignatureRow,
} from './components';
import transitionStudentService from '@/services/pei/transitionStudent.js';
import peiTransitionStudentStatus from '@/utils/constants/pei/peiTransitionStudentStatus.js';
import academicProgressLevels from '@/utils/constants/pei/academicProgressLevels.js';
import objectiveTypes from '@/utils/constants/pei/transition-student/transitionStudentSAEE12aObjectiveType.js';
import signatureService from '@/modules/common/signature.api';
import CustomCheckboxSingleOption from 'custom-checkbox-single-option';
import CustomError from 'custom-error';
import store from '@/store/index';
import objectToFormData from 'object-to-formdata';
import reportService from '@/services/report';

const SAEE12B_ROUTE = 'transition-student-12b';
export default {
  name: 'TransitionStudentProfile',
  components: {
    draggable,
    PageHeader,
    Board,
    BackupDocument,
    Areas,
    Objective,
    ParticipantSignatures,
    ParticipantSignatureRow,
    CustomCheckboxSingleOption,
    CustomError,
    SectionTitle,
  },
  mixins: [alertMixin],
  data: () => ({
    page: {
      icon: 'fas fa-user-edit',
      title: 'SAEE-12a Perfil del estudiante en proceso de transición escolar',
      routes: [
        'Inicio',
        'PEI',
        'Perfil del estudiante en proceso de transición escolar',
      ],
    },
    getParticipantSignatures: false,
    termsAccepted: false,
    componentKey: 0,
    signatureList: [],
    backupDocumentsData: [],
    availableSignatureOptions: [],
    participationTypeList: [],
    alternateParticipationTypeList: [],
    signatureInfoMessage: '',
    modelInfo: {
      PeiId: null,
      agencies: [],
      familyParticipationAct: '',
      familyProgressAct: '',
      revisionDate: null,
      studentRight: null,
      signatures: [],
      objectives: [],
    },
    detail: {},
    validated: false,
    removedDocuments: [],
    documentsReloadKey: 0,
  }),
  computed: {
    readOnly() {
      return this.detail.status === peiTransitionStudentStatus.Sometido;
    },
    headerActionConfig() {
      return [
        {
          text: 'Añadir Actividad',
          icon: 'fas fa-file-plus',
          variant: 'outline-success',
          visible: !this.readOnly,
          click: () => this.goToSaee12B(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'outline-success',
          visible: !this.readOnly,
          click: () => this.handleSubmit(),
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag',
          variant: 'outline-success',
          visible: !this.readOnly,
          click: () => this.handleSubmit(true),
        },
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.$router.go(-1),
        },
        {
          text: 'Imprimir',
          icon: 'fas fa-print',
          variant: 'outline-success',
          visible: this.readOnly,
          click: () => this.print(),
        },
      ];
    },
    validationTriggered() {
      return store.getters.getPeivalidationTriggered;
    },
    isDraft() {
      return true;
    },
    generalInfoBoard() {
      return [
        [
          {
            label: 'Año Académico:',
            icon: 'fas fa-address-book',
            data: this.detail.generalInfo?.academicYear,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Nombre del Estudiante:',
            icon: 'fas fa-address-book',
            data: `${this.detail.generalInfo?.studentSieId} - ${this.detail.generalInfo?.studentName}`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Impedimento:',
            icon: 'fas fa-ballot-check',
            data: this.detail.generalInfo?.impediment,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Número de PEI:',
            icon: 'fas fa-ballot-check',
            data: this.peiIdFormatted,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Escuela:',
            icon: 'fas fa-school',
            data: `${this.detail.generalInfo?.schoolCode} - ${this.detail.generalInfo?.schoolName}`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Estatus:',
            icon: 'fas fa-calendar-day',
            data: this.statusText,
            cols: 'col-12 col-md-4',
          },
        ],
      ];
    },
    peiIdFormatted() {
      if (!this.detail.generalInfo?.peiId) return;
      const originalString = this.detail.generalInfo?.peiId;
      return originalString.slice(0, 4) + '-' + originalString.slice(4);
    },
    statusText() {
      const { status } = this.detail;
      if (!status) return;
      switch (status) {
        case peiTransitionStudentStatus.Nuevo:
          return 'Nuevo';
        case peiTransitionStudentStatus.Sometido:
          return 'Sometido';
        case peiTransitionStudentStatus.Cancelado:
          return 'Cancelado';
        default:
          return '-';
      }
    },
    id() {
      return Number(this.$route.params.id);
    },
  },
  methods: {
    onDisplayArea(area) {
      area.displayed = !area.displayed;
    },

    gettingBackupDocument(data, key, area) {
      data.areaId = area.areaId;
      data.subAreaId = area.subAreaId;
      this.backupDocumentsData[key] = data;
      this.validateAreas();
    },
    async setSignatureConfig() {
      const requiredSignatures = [1, 4, 5];
      let signatureConfig = [];
      await signatureService
        .getDefaultConfiguration(
          this.peiIdFormatted,
          this.detail.generalInfo?.studentSieId
        )
        .then((result) => {
          signatureConfig = result.data;
        });
      this.signatureList = [];
      if (signatureConfig.length > 0) {
        this.signatureInfoMessage = signatureConfig.find(
          (e) => e.name != ''
        )?.name;
        signatureConfig.forEach((config) => {
          let signature = this.getBasicSignature(config.id, config.value);
          if (signature != undefined) {
            this.signatureList.push(signature);
          }
        });
      } else {
        this.signatureList = [];

        if (this.detail.signatures?.length > 0) {
          requiredSignatures.forEach((groupId) => {
            let signature = this.getBasicSignature(groupId);
            if (
              signature != undefined &&
              this.detail.signatures.find(
                (e) => e.groupId == groupId && !e.isRemovable
              ) == undefined
            ) {
              this.signatureList.push(signature);
            }
          });
          this.signatureList.push(
            ...this.detail.signatures.map((e) => ({
              id: e.id,
              signature: {
                name: e.fullName,
                positionId: {
                  signaturePositionId: e.peiSignaturePositionCodeId,
                },
                participationId: { id: e.participationTypeId },
                alternateId: { id: e.alternateParticipationTypeId },
              },
              groupId: e.groupId,
              isRequired: !e.isRemovable,
              isRemovable: e.isRemovable,
              isDraft: this.isDraft,
              isDisabled: !this.isDraft,
              isBasic: true,
            }))
          );
        } else {
          let signature1 = this.getBasicSignature(1);
          if (signature1 != undefined) {
            this.signatureList.push(signature1);
          }

          let signature5 = this.getBasicSignature(5);
          if (signature5 != undefined) {
            this.signatureList.push(signature5);
          }

          let signature4 = this.getBasicSignature(4);
          if (signature4 != undefined) {
            this.signatureList.push(signature4);
          }
        }
      }
    },
    async getParticipationTypeList() {
      await this.$ApiGet('/api/pei/participationTypeList')
        .then((res) => {
          this.participationTypeList = res.data;
        })
        .catch((err) => {
          this.response = err;
        });
    },
    async getAlternateParticipationTypeList() {
      await this.$ApiGet('/api/pei/alternateParticipationTypeList')
        .then((res) => {
          this.alternateParticipationTypeList = res.data;
        })
        .catch((err) => {
          this.response = err;
        });
    },
    async getAvailableSignatureOptions() {
      let self = this;
      let promise = new Promise((resolve) => {
        self.$ApiGet('/api/signature/availablepositionsTab9').then((res) => {
          self.availableSignatureOptions = res.data;
          resolve(res.data);
        });
      });

      return promise;
    },
    getBasicSignature(groupId, removable = false) {
      let signature = this.modelInfo.signatures.find((x) =>
        this.availableSignatureOptions.some(
          (y) =>
            y.signatureGroupId == groupId &&
            y.signaturePositionId == x.positionCode
        )
      );
      if (signature != undefined) {
        return {
          id: signature.id,
          signature: {
            name: signature.fullName,
            positionId: { signaturePositionId: signature.positionCode },
            participationId: { id: signature.participationTypeId },
            alternateId: { id: signature.alternateParticipationTypeId },
          },
          groupId: groupId,
          isRequired: false,
          isRemovable: removable,
          isDraft: this.isDraft,
          isDisabled: !this.isDraft,
          isBasic: true,
        };
      } else {
        if (this.isDraft) {
          this.signatureCounter -= 1;
          return {
            id: this.signatureCounter,
            signature: {
              name: '',
              positionId: undefined,
              participationId: undefined,
              alternateId: undefined,
            },
            groupId: groupId,
            isRequired: false,
            isRemovable: removable,
            isBasic: true,
          };
        }
      }
    },
    addSignature() {
      this.signatureCounter -= 1;
      this.signatureList.push({
        id: this.signatureCounter,
        signature: {
          name: '',
          positionId: undefined,
          participationId: undefined,
          alternateId: undefined,
        },
        groupId: 0,
        isRequired: false,
        isRemovable: true,
        isDraft: this.isDraft,
        isDisabled: !this.isDraft,
      });
    },
    async getDetails() {
      try {
        const { peiId, id } = this.$route.params;
        if (!peiId) return;
        const { data } = await transitionStudentService.getDetailSAEE12a({
          peiId,
          id,
        });
        this.backupDocumentsData = [];
        data.areas = data.areas.map((x) => {
          const strengthsLevels = [
            academicProgressLevels.L3,
            academicProgressLevels.L4,
            academicProgressLevels.L5,
          ];
          const needsLevels = [
            academicProgressLevels.L0,
            academicProgressLevels.L1,
            academicProgressLevels.L2,
          ];
          const value = {
            ...x,
            displayed: false,
            title: x.subAreaName
              ? `${x.areaName}: ${x.subAreaName}`.trim()
              : x.areaName,
            criterias: x.criterias.map((c) => {
              return { ...c, valid: true };
            }),
            strengths: x.objectives.filter(({ levelId, type }) => {
              return type
                ? type === objectiveTypes.Strength
                : strengthsLevels.includes(levelId);
            }),
            needs: x.objectives.filter(({ levelId, type }) => {
              return type
                ? type === objectiveTypes.Need
                : needsLevels.includes(levelId);
            }),
          };
          this.backupDocumentsData.push({
            files: [],
            types: [],
          });
          delete value.objectives;
          return value;
        });

        this.detail = data;
        this.termsAccepted = data.isCertified;
        this.onChangeObjectives();
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ?? error?.message,
          'error'
        );
      }
    },
    goToSaee12B() {
      this.$router.push({
        name: SAEE12B_ROUTE,
      });
    },
    async onQuitObjective(key, array) {
      try {
        const answer = await this.ShowCancelModal(
          'Confirmación',
          '¿Está seguro que desea proceder con la eliminación del criterio?',
          'Si',
          'No'
        );

        if (!answer) return;
        array.splice(key, 1);
        this.onChangeObjectives();
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ?? error?.message,
          'error'
        );
      }
    },
    onChangeObjectives() {
      const saeeId = Number(this.$route.params.id);
      this.modelInfo.objectives = this.detail.areas.flatMap((x) => {
        const objective = {
          peiTransitionStudentSAEE12aId: saeeId,
          peiAreaId: x.areaId,
          peiSubAreaId: x.subAreaId,
        };
        const strengths = x.strengths.map((s) => {
          return {
            ...objective,
            type: objectiveTypes.Strength,
            peiProgramServiceObjectiveId: s.objectiveId,
            academicProgressLevelId: s.levelId,
          };
        });

        const needs = x.needs.map((s) => {
          return {
            ...objective,
            type: objectiveTypes.Need,
            peiProgramServiceObjectiveId: s.objectiveId,
            academicProgressLevelId: s.levelId,
          };
        });

        if (this.validated) {
          x.criterias[1].valid = Boolean(x.strengths.length);
          x.criterias[2].valid = Boolean(x.needs.length);
        }

        return strengths.concat(needs);
      });
    },
    validateAreas() {
      if (!this.validated) {
        return;
      }

      const backUpDocuments = this.backupDocumentsData.flatMap(
        ({ types, areaId, subAreaId }) =>
          types.map((e) => {
            return {
              ...e,
              areaId,
              subAreaId,
            };
          }) ?? []
      );

      this.detail.areas.forEach((area) => {
        area.criterias[0].valid = backUpDocuments.some((d) => {
          return d.areaId === area.areaId && d.subAreaId === area.subAreaId;
        });
        area.criterias[1].valid = true;
        area.criterias[2].valid = true;
      });
    },
    async handleSubmit(isSubmit = false) {
      if (isSubmit) {
        if (!this.termsAccepted) {
          this.ShowToast(
            'Error',
            'Favor certificar los términos y responsabilidades',
            'error'
          );
          return;
        }

        this.validated = true;
        this.validateAreas();
        const allCriterias = this.detail.areas.flatMap(({ criterias }) =>
          criterias.map(({ valid }) => valid)
        );
        const criteriasValid = allCriterias.every((valid) => valid);
        if (!criteriasValid) {
          this.ShowToast(
            'Error',
            'Favor seleccionar al menos un criterio para completar el proceso',
            'error'
          );
          return;
        }

        this.signatureList.forEach((x) => (x.isRequired = true));
        const { getGroupName } = this.$refs.ParticipantSignatures;
        const invalidSignatureRequired = this.signatureList
          ?.filter(({ groupId }) => [1, 5, 4].includes(groupId))
          .map((item) => {
            const { name, participationId, positionId } = item.signature;
            const valid =
              Boolean(name) && Boolean(participationId) && Boolean(positionId);
            return !valid ? getGroupName(item) : '';
          });
        const signaturesInvalid = invalidSignatureRequired?.some((x) =>
          Boolean(x)
        );
        if (signaturesInvalid) {
          const signatureLis = invalidSignatureRequired
            .map((x) => `<li>${x}</li>`)
            .join('');
          this.ShowToast(
            'Error',
            `Todos las participantes son requeridos: <ol> ${signatureLis} </ol>`,
            'error'
          );
          return;
        }
      }
      const { objectives } = this.modelInfo;

      const signatures = this.getSignatureList().filter(
        (x) =>
          Boolean(x.fullName) &&
          Boolean(x.participationTypeId) &&
          Boolean(x.peiSignaturePositionCodeId)
      );
      const { id } = this.$route.params;
      try {
        const payload = {
          id: Number(id),
          isSubmit,
          objectives,
          signatures,
          isCertified: this.termsAccepted,
        };
        const formData = objectToFormData(payload, { indices: true });
        const changedDocuments = this.backupDocumentsData.flatMap((x) =>
          (x?.files ?? [])
            .filter((file) => file instanceof File || file.newlySelected)
            .map((file) => {
              const index = x.files.indexOf(file);
              return {
                file,
                details: {
                  ...x.types[index],
                  areaId: x.areaId,
                  subAreaId: x.subAreaId,
                },
              };
            })
        );
        this.removedDocuments?.forEach((document) => {
          changedDocuments.push(document);
        });

        changedDocuments.forEach((document, index) => {
          const keyName = `backUpDocuments[${index}]`;
          const isNewFile = document.file instanceof File && document.file.name;
          if (isNewFile) {
            formData.append(`${keyName}.attachment`, document.file);
            formData.append(`${keyName}.fileName`, document.file?.name);
          }

          const fileDetails = document.details;

          if (document?.file?.deleteFromServer === true && document?.file?.id) {
            formData.append(`${keyName}.deleteFromServer`, true);
            formData.append(
              `${keyName}.DocumentIdToDelete`,
              document?.file?.id
            );
          }

          formData.append(
            `${keyName}.documentDate`,
            fileDetails?.documentRawDate ?? ''
          );
          formData.append(
            `${keyName}.evaluationDate`,
            fileDetails?.documentRawDate ?? ''
          );
          formData.append(
            `${keyName}.disciplineTypeName`,
            fileDetails?.disciplineTypeName ?? ''
          );
          formData.append(
            `${keyName}.disciplineTypeId`,
            fileDetails?.disciplineTypeId ?? ''
          );
          formData.append(
            `${keyName}.documentTypeId`,
            fileDetails?.documentTypeId ?? ''
          );
          formData.append(`${keyName}.comment`, fileDetails?.comment ?? '');
          formData.append(
            `${keyName}.administrator`,
            fileDetails?.administrator ?? ''
          );
          formData.append(
            `${keyName}.serviceTypeName`,
            fileDetails?.serviceTypeName ?? ''
          );
          formData.append(`${keyName}.areaId`, fileDetails?.areaId ?? '');
          formData.append(`${keyName}.subAreaId`, fileDetails?.subAreaId ?? '');
          formData.append(
            `${keyName}.studentSieId`,
            this.detail.generalInfo?.studentSieId ?? ''
          );
        });
        await transitionStudentService.submitSAEE12a(formData);
        this.ShowToast(
          'Éxito',
          'Información ha sido guardada exitosamente.',
          'success'
        );
        this.removedDocuments = [];
        this.backupDocumentsData.forEach((x) => {
          if (!x) return;
          const toRemove = (x.files ?? []).reduce((acc, file, idx) => {
            if (file instanceof File || file.newlySelected) acc.push(idx);
            return acc;
          }, []);
          toRemove.reverse().forEach((idx) => {
            x.files.splice(idx, 1);
            x.types.splice(idx, 1);
          });
          (x.files ?? []).forEach((file) => {
            file.deleteFromServer = false;
            delete file.newlySelected;
          });
        });
        const areasDisplayed = this.detail.areas.filter((x) => x.displayed);
        await this.initData();
        this.detail.areas.forEach((x) => {
          x.displayed = areasDisplayed.some(
            (y) => y.areaId === x.areaId && y.subAreaId === x.subAreaId
          );
        });
        this.documentsReloadKey++;
        this.$nextTick(() => {
          this.$refs.detailAreas?.syncBootstrapCollapse();
        });
      } catch (err) {
        this.ShowToast(
          'Error',
          err?.response?.data?.message ?? err?.message,
          'error'
        );
      }
    },
    // Getting data from ParticipantSignatures component.
    async gettingParticipantSignatures(data) {
      this.getParticipantSignatures = false;
      return this.agenciesDTOTransformations(JSON.parse(JSON.stringify(data)));
    },
    async deleteSignature(id) {
      let signature = this.signatureList.find((e) => e.id === id);

      if (signature != null && signature.isBasic == true) {
        await signatureService.removeFromDefaults(
          signature.groupId,
          this.peiId
        );
      }
      let signatureListIndex = this.signatureList.findIndex((e) => e.id === id);
      this.signatureList.splice(signatureListIndex, 1);
    },
    loadSignatures() {
      let self = this;
      this.modelInfo.signatures.forEach(function(signature) {
        if (!self.signatureList.some((x) => x.id == signature.id)) {
          self.signatureList.push({
            id: signature.id,
            signature: {
              name: signature.fullName,
              positionId: { signaturePositionId: signature.positionCode },
              participationId: { id: Number(signature.participationTypeId) },
              alternateId: { id: signature.alternateParticipationTypeId },
            },
            groupId: 0,
            isRequired: false,
            agency: signature.participationAgencyId
              ? { selectedAgency: { id: signature.participationAgencyId } }
              : undefined,
            isRemovable: signature.participationAgencyId ? false : true,
            isDisabled: !self.isDraft ? true : false,
            nameDisabled: signature.participationAgencyId ? true : false,
            participationDisabled: signature.participationAgencyId
              ? true
              : false,
            positionDisabled: signature.participationAgencyId ? true : false,
            isDraft: self.isDraft,
          });
        }
      });
    },
    getSignatureList() {
      const signatures = this.signatureList.map((x) => {
        return {
          groupId: x.groupId,
          fullName: x.signature.name,
          peiSignaturePositionCodeId:
            x.signature.positionId?.signaturePositionId,
          participationTypeId: x.signature.participationId?.id,
          alternateParticipationTypeId: x.signature.alternateId?.id,
          isRemovable: x.isRemovable,
        };
      });
      return signatures;
    },
    addAgencySignature(agency, participationType) {
      this.signatureCounter -= 1;
      this.signatureList.push({
        id: this.signatureCounter,
        signature: {
          name: agency.contactName,
          positionId: { signaturePositionId: 849 },
          participationId: { id: participationType },
          alternateId: undefined,
        },
        groupId: 0,
        isRequired: false,
        agency: agency,
        isRemovable: false,
        isDisabled: false,
        positionDisabled: true,
        participationDisabled: true,
        nameDisabled: true,
      });
    },
    onDocumentsChange(documentId) {
      if (documentId) {
        for (const entry of this.backupDocumentsData) {
          if (!entry) continue;
          const fileIndex = entry.files?.findIndex((f) => f.id === documentId);
          if (fileIndex !== -1 && fileIndex !== undefined) {
            this.removedDocuments.push({
              file: { ...entry.files[fileIndex], deleteFromServer: true },
              details: {
                ...entry.types[fileIndex],
                areaId: entry.areaId,
                subAreaId: entry.subAreaId,
              },
            });
            break;
          }
        }
      }
      if (!this.validated) return;
      const backUpDocuments = this.backupDocumentsData.flatMap(
        ({ types, areaId, subAreaId }) =>
          types.map((e) => {
            return {
              ...e,
              areaId,
              subAreaId,
            };
          }) ?? []
      );
      this.detail.areas.forEach((area) => {
        area.criterias[0].valid = backUpDocuments.some((d) => {
          return d.areaId === area.areaId && d.subAreaId === area.subAreaId;
        });
      });
    },
    async initData() {
      this.termsAccepted = false;
      this.validated = false;
      await this.getDetails();
      await this.getAvailableSignatureOptions();
      await this.getParticipationTypeList();
      await this.getAlternateParticipationTypeList();
      await this.setSignatureConfig();
    },
    async print() {
      await reportService.downloadSaee12a(this.id);
    },
  },
  async mounted() {
    await this.initData();
  },
};
</script>
<style scoped lang="scss">
.tag-badge {
  display: inline-block;
  padding-right: 1em;
  height: 42px;
  background: transparent linear-gradient(90deg, #ffffff 0%, #e8e8e8 100%) 0% 0%
    no-repeat padding-box;
  border-radius: 26px;
  cursor: pointer;
  margin-bottom: 10px;
}

.dot {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: inline-block;
  /* top: 268px; */
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  padding-top: 9px;
}
.orange-icon {
  color: #ff7058;
}

.orange-icon-badge-align {
  vertical-align: top;
}

.tag-badge-title {
  text-align: left;
  font: Bold 25px/30px Lato;
  letter-spacing: 0;
  color: #11291b;
  display: inline-block;
}

.border-box {
  border: 1px solid #e2e2e2;
  border-radius: 5px 5px 0px 0px;
  padding: 20px;
  width: 100%;
}
.border-box.gray {
  border-radius: 0px 0px 5px 0px;
  background: #f2f2f2 0% 0% no-repeat padding-box;
  border-top: none;
  padding: 20px 0;
}
.border-box.gray.error {
  background: #ffebeb 0% 0% no-repeat padding-box;
  border: 1px solid #c9545e;
}
.invalid {
  text-align: left;
  font: Lato, Regular;
  letter-spacing: 0;
  color: #ff5b5c;
  margin-bottom: 5px;
  font-weight: normal;
}
i.far.fa-check-circle {
  font-size: 24px;
  padding-right: 4px;
  line-height: 18px;
  margin-left: 4px;
}
.custom-control {
  margin-left: 20px; /* This moves the checkbox to the right */
}
.signed {
  background-color: #2db782;
  color: white;
}
.unsigned {
  background-color: #f7f7f7;
  color: black;
}
::v-deep .checkbox-word-container p,
::v-deep .title-container p {
  font-weight: bold !important;
}
.text-danger ::v-deep .tag-badge-title-info {
  color: #c9545e !important;
}
.participant-signatures {
  .participant {
    padding: 1rem;
  }
}
</style>
